import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PrintCardDomainModel } from '../../../../domain/print-card-domain-model';
import { RenderContentViewModel } from '../../base/render-content-view-model';
import { combineLatest, iif, Observable } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { CardData } from '../../../../models/print-cards/card-data';
import { SectionRowViewModelUtils } from '../../../../utils/section-row-view-model-utils';
import { ShelfTalkerCardData } from '../../../../models/shelf-talkers/shelf-talker-card-data';
import { exists } from '../../../../functions/exists';
import { ShelfTalkerUtils } from '../../../../utils/shelf-talker-utils';
import { VariantAssetService } from '../../../services/variant-asset-service';

@Injectable()
export class RenderCardPreviewViewModel extends RenderContentViewModel {
  constructor(
    router: Router,
    activatedRoute: ActivatedRoute,
    printCardDomainModel: PrintCardDomainModel,
    variantAssetService: VariantAssetService
  ) {
    super(router, activatedRoute, printCardDomainModel, variantAssetService);
  }

  protected readonly printCardGrouping$ = combineLatest([
    this.printCardMenu$,
    this.cardStacks$,
    this.overrideVariantIds$,
    this.companyConfig$,
    this.locationConfig$
  ]).pipe(
    map(([menu, cardStacks, variantIds, companyConfig, locationConfig]) => {
      const cardStack = cardStacks?.firstOrNull();
      const generateCardData = SectionRowViewModelUtils.generateRowViewModels;
      return generateCardData(cardStack, menu, companyConfig, locationConfig, cardStack?.products, [], variantIds)
        ?.filter((card): card is CardData => card instanceof CardData)
        ?.filter(cardData => cardData?.rowVariants?.map(v => v?.id)?.intersection(variantIds)?.length > 0)
        ?.firstOrNull();
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  protected readonly shelfTalkerCardGrouping$ = combineLatest([
    this.shelfTalkerMenu$,
    this.overrideSectionIds$,
    this.companyConfig$,
    this.locationConfig$
  ]).pipe(
    map(([menu, sectionIds, companyConfig, locationConfig]) => {
      const generateData = ShelfTalkerUtils.generateShelfTalkerCardData;
      return generateData(menu, companyConfig, locationConfig, sectionIds)
        ?.filter((data): data is ShelfTalkerCardData => data instanceof ShelfTalkerCardData)
        ?.firstOrNull();
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * Note: overrideSiblingVariantIds are only needed for rendering smart prints.
   */
  public readonly cardGrouping$: Observable<CardData | ShelfTalkerCardData> = this.shelfTalkerMenu$.pipe(
    map(shelfTalkerMenu => exists(shelfTalkerMenu)),
    switchMap(isShelfTalker => iif(() => isShelfTalker, this.shelfTalkerCardGrouping$, this.printCardGrouping$)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public layoutInstructions$ = this.cardGrouping$.pipe(
    map(cardGrouping => cardGrouping?.getLayoutInstructions()),
    shareReplay({ bufferSize: 1, refCount: true })
  );
}
